import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Person} from 'src/context/ArrivalContext';
import {Input} from 'src/components/form/input/Input';
import {postValidateIdentity, fetchClinicById} from 'src/services/api/api';
import {Checkbox} from 'src/components/form/checkbox/Checkbox';

import './DropinModalPeople.scss';
import {validateEmail, validatePhone} from 'src/services/validation/validation';
import {Button} from 'src/components/form/button/Button';
// import ModalPortal from 'src/components/layout/modal/ModalPortal';
import {ClinicJSON} from '../../../../services/api/types/types';
import {Radio} from 'src/components/form/radio/Radio';
import {getSystemCountry} from 'src/services/i18n/locale';
import {getIdentityImpl} from 'src/services/i18n/identity';

interface DropinPersonProps {
    index: number;
    person: Person;
    onChange(updatedPerson: Person): void;
    headline: string;
    ageRange?: [minAge: number | null, maxAge: number | null];
    showContacts: boolean;
    onCopyContacts?(phone: string, email: string, marketing?: boolean): void;
}

const identiyImpl = getIdentityImpl();

export function DropinModalPerson({
    index,
    person,
    onChange,
    headline,
    ageRange = [null, null],
    showContacts,
    onCopyContacts
}: DropinPersonProps) {
    const {t} = useTranslation();
    const givenName = person.givenName;
    const familyName = person.familyName;
    const identity = person.identity;
    const phone = person.contactInfo?.mobile || '';
    const email = person.contactInfo?.email || '';
    const marketing = person.contactInfo?.marketing;
    const [showBirthdateInput, setShowBirthdateInput] = useState(false);
    const [identityValid, setIdentityValid] = useState(true);
    const [ageValid, setAgeValid] = useState(true);
    const [givenNameValid, setGivenNameValid] = useState(true);
    const [familyNameValid, setFamilyNameValid] = useState(true);
    const isPhoneValid = validatePhone(phone);
    const isEmailValid = validateEmail(email);
    const [minAge, maxAge] = ageRange;

    const handleUpdate = (field: keyof Person, value: string | object) => {
        const updatedPerson: Person = {
            ...person,
            [field]: value
        };

        onChange(updatedPerson);
    };

    const ageError = () => {
        if (minAge && maxAge) {
            return t('arrival.identity_age_range_error', {minAge, maxAge});
        } else if (minAge) {
            return t('arrival.identity_age_over_error', {minAge});
        } else if (maxAge) {
            return t('arrival.identity_age_under_error', {maxAge});
        }

        return '';
    };

    const pnrTimeout = useRef<NodeJS.Timeout>();
    const identityValidation = async (identity: string, birthdate: boolean) => {
        clearTimeout(pnrTimeout.current);

        if (identity.length === 0) {
            const updatedPerson = {
                ...person,
                identity: identity,
                identityValid: false
            };
            setIdentityValid(false);
            onChange(updatedPerson);

            return;
        }

        // Debounced pnr validation
        pnrTimeout.current = setTimeout(async () => {
            if ((birthdate && identity.length !== 8) || (!birthdate && !identiyImpl.isNumberComplete(identity))) {
                const updatedPerson = {
                    ...person,
                    identity: identity,
                    identityValid: false
                };
                setIdentityValid(false);
                onChange(updatedPerson);

                return;
            }

            try {
                const validationResults = await postValidateIdentity(identity);
                const {age} = validationResults;
                const ageValid = age >= (minAge || 0) && age <= (maxAge || 1000);
                const updatedPerson = {
                    ...person,
                    gender: validationResults.gender,
                    age: validationResults.age,
                    identity: identity,
                    identityValid: ageValid
                };

                setIdentityValid(true);
                setAgeValid(ageValid);
                onChange(updatedPerson);
            } catch (error) {
                const updatedPerson = {
                    ...person,
                    identity: identity,
                    identityValid: false
                };

                setIdentityValid(false);
                setAgeValid(true);
                onChange(updatedPerson);
            }
        }, 500);
    };

    const systemCountry = getSystemCountry();

    return (
        <>
            <h2 style={{marginTop: '16px'}}>{headline}</h2>
            <div className='person-form-container'>
                <div className='people-form__name-container--name-input'>
                    <Input
                        onChange={(val) => {
                            handleUpdate('givenName', val);
                            setGivenNameValid(val.length > 0);
                        }}
                        onBlur={(val) => setGivenNameValid(val.length > 0)}
                        isValid={givenNameValid}
                        type='text'
                        id={`first-name-${index}`}
                        value={givenName}
                        required
                        label={t('people_select.people_form.givenName_label')}
                    />
                    {!givenNameValid && (
                        <p className='input-error'>{t('people_select.people_form.givenName_validation_error')}</p>
                    )}
                </div>
                <div className='people-form__name-container--name-input'>
                    <Input
                        onChange={(val) => {
                            handleUpdate('familyName', val);
                            setFamilyNameValid(val.length > 0);
                        }}
                        onBlur={(val) => setFamilyNameValid(val.length > 0)}
                        isValid={familyNameValid}
                        type='text'
                        id={`last-name-${index}`}
                        value={familyName}
                        required
                        label={t('people_select.people_form.familyName_label')}
                    />
                    {!familyNameValid && (
                        <p className='input-error'>{t('people_select.people_form.familyName_validation_error')}</p>
                    )}
                </div>
                <div className='people-form__name-container--name-input'>
                    <Input
                        onChange={(val) => {
                            handleUpdate('identity', val);
                            identityValidation(val, showBirthdateInput);
                        }}
                        onBlur={(val) => identityValidation(val, showBirthdateInput)}
                        isValid={identityValid || identity.length === 0}
                        type='text'
                        id={`pnr-${index}`}
                        ariaLabel={t(`people_select.people_form.identity_aria_label.${systemCountry}`)}
                        placeholder={
                            showBirthdateInput
                                ? t(`people_select.people_form.identity_placeholder_birthdate.${systemCountry}`)
                                : t(`people_select.people_form.identity_placeholder.${systemCountry}`)
                        }
                        value={identity}
                        required
                        label={
                            showBirthdateInput
                                ? t(`people_select.people_form.identity_label_birthdate.${systemCountry}`)
                                : t(`people_select.people_form.identity_label.${systemCountry}`)
                        }
                    />

                    {!identityValid && identity.length > 0 && (
                        <p className='input-error'>
                            {showBirthdateInput
                                ? t(`people_select.people_form.identity_validation_error_birthdate.${systemCountry}`)
                                : t(`people_select.people_form.identity_validation_error.${systemCountry}`)}
                        </p>
                    )}
                    {!ageValid && <p className='input-error'>{ageError()}</p>}
                </div>
                <Checkbox
                    onClick={(bool) => {
                        setShowBirthdateInput(bool);
                        identityValidation(identity, bool);
                    }}
                    id={`checkbox-identity-input-${index}`}
                    checked={showBirthdateInput}
                    label={t(`arrival.no_personal_identity_number.${systemCountry}`)}
                />
                {showContacts && (
                    <>
                        <div className='people-form__name-container--name-input'>
                            <Input
                                type='tel'
                                id={`phone-input-${index}`}
                                isValid={isPhoneValid}
                                required
                                label={t('confirm.form.telephone')}
                                value={phone}
                                onChange={(val) => {
                                    handleUpdate('contactInfo', {
                                        mobile: val,
                                        email: email,
                                        marketing: marketing
                                    });
                                }}
                            />
                            {!isPhoneValid && (
                                <p className='input-error'>{t(`confirm.form.phone_error.${systemCountry}`)}</p>
                            )}
                        </div>
                        <div className='people-form__name-container--name-input'>
                            <Input
                                type='email'
                                id={`email-input-${index}`}
                                isValid={isEmailValid}
                                label={t('confirm.form.email')}
                                value={email}
                                onChange={(val) => {
                                    handleUpdate('contactInfo', {
                                        mobile: phone,
                                        email: val,
                                        marketing: marketing
                                    });
                                }}
                            />
                            {!isEmailValid && <p className='input-error'>{t('confirm.form.email_error')}</p>}
                        </div>
                        <div>
                            {t('confirm.policy.storing_data_non_optional')}
                            <div className='radio-group' role='radiogroup'>
                                <Radio
                                    checked={marketing === true}
                                    name={`consent-${index}`}
                                    label={t('consent.yes')}
                                    onClick={() => {
                                        handleUpdate('contactInfo', {
                                            mobile: phone,
                                            email: email,
                                            marketing: true
                                        });
                                    }}
                                />
                                <Radio
                                    checked={marketing === false}
                                    name={`consent-${index}`}
                                    label={t('consent.no')}
                                    onClick={() => {
                                        handleUpdate('contactInfo', {
                                            mobile: phone,
                                            email: email,
                                            marketing: false
                                        });
                                    }}
                                />
                            </div>
                        </div>
                        {onCopyContacts && (
                            <div>
                                <Button
                                    thin
                                    variant='outlined'
                                    onClick={() => {
                                        onCopyContacts(phone, email, marketing);
                                    }}
                                >
                                    {t('arrival.dropin_copy_contact_details_to_all')}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
}
